/**
 * Zustand store for the Mist diagnostic system.
 *
 * Single source of truth for all diagnostics across the IDE.
 * All UI consumers (Monaco, Problems panel, status bar, deploy,
 * simulation, adversarial, graph overlay) read from this store.
 */

import { create } from 'zustand';
import type {
  MistDiagnostic,
  MistDiagnosticResult,
  MistPhaseStatus,
} from '../editor/diagnosticTypes';
import { aggregateResult, getPhaseStatus } from '../editor/diagnosticTypes';
import type { PviCompileResult } from '../editor/pviTypes';

interface DiagnosticState {
  /** Current aggregated diagnostic result. */
  result: MistDiagnosticResult;
  /** Current phase status (Clean, SyntaxError, etc.). */
  phaseStatus: MistPhaseStatus;
  /** Whether analysis is currently running. */
  analyzing: boolean;
  /** Active source file name. */
  activeFile: string | null;
  /** Latest PVI compile result (kept alongside diagnostics). */
  pviResult: PviCompileResult;

  /** Replace all diagnostics for the active file. */
  setDiagnostics: (diagnostics: MistDiagnostic[], pviResult: PviCompileResult, fileName: string) => void;
  /** Merge server-side compile errors into the existing diagnostics. */
  addServerDiagnostics: (diagnostics: MistDiagnostic[]) => void;
  /** Set analyzing state. */
  setAnalyzing: (analyzing: boolean) => void;
  /** Clear all diagnostics. */
  clear: () => void;
}

const EMPTY_RESULT: MistDiagnosticResult = {
  diagnostics: [],
  hasErrors: false,
  isDeployBlocked: false,
  isSimulationBlocked: false,
  isGraphBlocked: false,
  highestFailedStage: null,
};

const EMPTY_PVI: PviCompileResult = {
  status: 'idle',
  graph: null,
  errors: [],
  warnings: [],
  timestamp: 0,
  stale: false,
};

export const useDiagnosticStore = create<DiagnosticState>((set) => ({
  result: EMPTY_RESULT,
  phaseStatus: 'Clean',
  analyzing: false,
  activeFile: null,
  pviResult: EMPTY_PVI,

  setDiagnostics: (diagnostics, pviResult, fileName) => {
    const result = aggregateResult(diagnostics);
    const phaseStatus = getPhaseStatus(result);
    set({ result, phaseStatus, pviResult, activeFile: fileName, analyzing: false });
  },

  addServerDiagnostics: (serverDiags) => {
    const current = useDiagnosticStore.getState();
    const merged = [...current.result.diagnostics, ...serverDiags];
    const result = aggregateResult(merged);
    const phaseStatus = getPhaseStatus(result);
    set({ result, phaseStatus });
  },

  setAnalyzing: (analyzing) => set({ analyzing }),

  clear: () => set({
    result: EMPTY_RESULT,
    phaseStatus: 'Clean',
    analyzing: false,
    activeFile: null,
    pviResult: EMPTY_PVI,
  }),
}));
